import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, RequestError, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { getFormattedPrice } from 'webapp/utils/number';

import CommonRequestTrialAddonButtonComponent from 'webapp/components/common/request-trial-addon-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class BillingPopupAddonTrialComponent extends PopupInnerModalComponent<PopupData.Billing.TrialAddon> {
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;

	addonFeatures = [
		'Company mission & vision alignment',
		'Objectives and goals overview',
		'Strategy map visualization',
		'Automatic progress tracking',
		'Team and company goal ownership',
	];

	get activePlan() {
		return this.billingService.activePlan;
	}

	get addonPrice() {
		const addonType = this.flowPromotionsService.getBillingAddonTypeForFeature(this.feature);
		if (!addonType) {
			return null;
		}

		const addonPriceRaw = this.activePlan?.getAddonPrice(addonType);
		return getFormattedPrice(addonPriceRaw || 0);
	}

	get feature() {
		return this.popupData.feature;
	}

	get addonId() {
		return this.billingService.getAddonIdForFeature(this.feature)!;
	}

	get featureName() {
		const feature = this.feature;
		return (feature && this.billingService.getFeatureName(feature)) || feature || null;
	}

	get cycleText() {
		return this.activePlan?.billingCycleTextLong;
	}

	get isOKRsAddon() {
		return this.feature === BillingFeature.OKR;
	}

	@action
	activateAddon() {
		this.submitInProgress = true;

		this.billingService
			.addAddon(this.addonId)
			.then(
				() => {
					this.infolayerService.ok('Addon added!');
				},
				(errorData: RequestError) => {
					this.requestService.handleGenericThrownError(errorData);
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			});
	}
	<template>
		<div class="content content--split content--addon">
			<div class="content__left">
				{{#if this.isOKRsAddon}}
					<div class="content__text-container">
						<img
							src="/images/placeholders/goals.svg"
							width="48"
							height="48"
							alt="Icon"
						/>
						<div class="content__text content__text--highlight">
							OKR strategy & multi-level objectives
						</div>
					</div>
					<div class="content__image-container">
						<img src="/images/billing/forbidden-feature/okrs.png" alt="Features" />
					</div>
				{{/if}}
			</div>
			<div class="content__right">
				<div class="content__text content__text--notice">
					This feature isn’t available on your current plan
				</div>
				<div class="content__text content__text--upgrade">
					Unlock
					<span>{{this.featureName}}</span>
					add-on
				</div>
				{{#if this.addonPrice}}
					<div class="content__pricing">
						<span class="content__pricing-amount">{{this.addonPrice}}</span>
						<span class="content__pricing-period">/{{this.cycleText}}</span>
					</div>
				{{/if}}
				<div class="content__features">
					{{#each this.addonFeatures as |feature|}}
						<div class="content__feature-item">
							<CommonUiIconComponent @name="Check" @size="md" />
							{{feature}}
						</div>
					{{/each}}
				</div>

				<div class="content__button-container">
					<CommonRequestTrialAddonButtonComponent
						@feature={{this.feature}}
						@isSubmitInProgress={{this.submitInProgress}}
						@size="big"
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Enable add-on now"
						@theme="blue-outlined"
						@onTrigger={{this.activateAddon}}
					/>
				</div>
			</div>
		</div>
	</template>
}
